import { ApiPath } from '@common/app/api-path.ts';
import {
  type BoostOne,
  type Quarry,
  type UserQuarry,
} from '@common/types/quarryType.ts';
import {
  type ClaimAllResourcesResponse,
  type ClaimResourceByIdResponse,
  type TapQuarryRequestType,
} from '@common/types/query/quarry.ts';

import { api } from '../base.ts';

import { addLocalStockUpdatedAtField } from './add-local-stock-updated-at-field.ts';

const quarriesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUserQuarriesForClaim: builder.query<Quarry, string | undefined>({
      query: () => ApiPath.USER_QUARRIES,
      providesTags: ['BOOST', 'QUARRY'],
      transformResponse: (response: Quarry) => ({
        ...response,
        userQuarries: response.userQuarries.map(
          addLocalStockUpdatedAtField(response.now),
        ),
      }),
    }),
    tapQuarry: builder.mutation<BoostOne, TapQuarryRequestType>({
      query: ({ quarryId, boosts, token }) => ({
        url: ApiPath.TAP_QUARRY + quarryId,
        method: 'POST',
        body: { boosts: boosts || 1 },
        headers: token ? { 'X-BOOST-TOKEN': token } : {},
      }),
      transformResponse: (response: BoostOne) => ({
        ...response,
        quarry: addLocalStockUpdatedAtField<UserQuarry>(response.now)(
          response.quarry,
        ),
      }),
    }),
    claimAllResources: builder.mutation<
      ClaimAllResourcesResponse,
      { adsView: boolean; paid: boolean } | void
    >({
      query: (body) => ({
        url: ApiPath.CLAIM_ALL_QUARRIES,
        method: 'POST',
        body: body || { adsView: false, paid: false },
      }),
      transformResponse: (response: ClaimAllResourcesResponse) => ({
        ...response,
        plants: response.plants.map(addLocalStockUpdatedAtField(response.now)),
      }),
    }),
    claimResourceById: builder.mutation<
      ClaimResourceByIdResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: ApiPath.CLAIM_RESOURCE + id,
        method: 'POST',
      }),
      transformResponse: (response: ClaimResourceByIdResponse) => ({
        ...response,
        plant: addLocalStockUpdatedAtField(response.now)(response.plant),
      }),
      invalidatesTags: ['QUARRY'],
    }),
    buildQuarry: builder.mutation<UserQuarry[], { id: string }>({
      query: ({ id }) => ({
        url: ApiPath.BUILD_QUARRY + id,
        method: 'POST',
      }),
      invalidatesTags: ['QUARRY'],
    }),
    upgradeQuarry: builder.mutation<UserQuarry, { id: string }>({
      query: ({ id }) => ({
        url: ApiPath.UPGRADE_QUARRY + id,
        method: 'PATCH',
      }),
      invalidatesTags: ['QUARRY'],
    }),
  }),
  overrideExisting: false,
});
const {
  useGetUserQuarriesForClaimQuery,
  useLazyGetUserQuarriesForClaimQuery,
  useClaimAllResourcesMutation,
  useBuildQuarryMutation,
  useUpgradeQuarryMutation,
  useClaimResourceByIdMutation,
  useTapQuarryMutation,
  endpoints: {
    getUserQuarriesForClaim: { useQueryState: useGetUserQuarriesForClaimState },
  },
} = quarriesApi;

export {
  quarriesApi,
  useBuildQuarryMutation,
  useClaimAllResourcesMutation,
  useClaimResourceByIdMutation,
  useGetUserQuarriesForClaimQuery,
  useGetUserQuarriesForClaimState,
  useLazyGetUserQuarriesForClaimQuery,
  useTapQuarryMutation,
  useUpgradeQuarryMutation,
};
